/**
 * "While you were away" — the catch-up for a returning player.
 *
 * A campaign has no session boundary to recap at, so this is the only thing
 * that answers "what happened while I was gone". It arrives on
 * `campaign:snapshot`, already filtered to what this character may see, and is
 * **absent entirely** when there is nothing to say — so this renders nothing
 * rather than announcing that nothing happened.
 *
 * **Dismissal is client-side and nothing is persisted.** The server's bookmark
 * moves when the last tab closes, so a digest dismissed and then reloaded into
 * would come back — which is correct: it is a view of a delta, not a message
 * with a read receipt. Making it stick would need a second bookmark that could
 * disagree with the first.
 *
 * Takes what it renders as props rather than reading the store, like every
 * other panel here. That is what makes it renderable in a test: zustand serves
 * the *initial* state to a server render, so a component that subscribed could
 * only ever be tested empty.
 *
 * `StoryPanel` sits beside it and answers the other half of the same question.
 * The digest is *what you personally missed*; the story is *what the campaign
 * is*, and it is the one a player with no scrollback at all needs — a newcomer,
 * or one who has just rerolled and whose participation window starts today.
 * Two panels rather than one, because a returning player wants the delta and
 * would have to hunt for it inside a recap of the whole campaign.
 */

export interface DigestView {
  readonly sinceSeq: number;
  readonly lines: readonly { readonly author: string; readonly text: string }[];
  readonly omitted: number;
  /** True when `omitted` is a floor: there may be more behind what was read. */
  readonly atLeast?: boolean;
}

export function DigestPanel({
  digest,
  onDismiss,
}: {
  readonly digest: DigestView | null;
  readonly onDismiss: () => void;
}) {
  if (!digest) return null;

  return (
    <section
      aria-label="While you were away"
      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950"
    >
      <div className="flex items-start justify-between gap-2">
        <h2 className="font-semibold">While you were away</h2>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-md border border-amber-400 px-2 py-0.5 text-xs dark:border-amber-700"
        >
          Dismiss
        </button>
      </div>

      {/* Stated rather than silently trimmed: "and 43 more" is the difference
          between a short catch-up and a wrong one. The `+` when the server
          could only give a floor — a confident wrong number is worse than a
          qualified right one. */}
      {digest.omitted > 0 && (
        <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
          {digest.omitted}
          {digest.atLeast ? '+' : ''} earlier{' '}
          {digest.omitted === 1 && !digest.atLeast ? 'beat is' : 'beats are'} not shown.
        </p>
      )}

      <ol className="mt-2 space-y-1">
        {digest.lines.map((line, i) => (
          // The index is part of the key on purpose: the same author can say
          // the same thing twice, and a digest is a list of beats rather than
          // of rows with ids of their own.
          <li key={`${line.author}-${i}`}>
            <span className="font-medium">{line.author}:</span> {line.text}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * "The story so far" — the rolling campaign summary.
 *
 * **Only for a player who cannot simply read up.** Shown when the transcript
 * they hold is empty or nearly so, which is exactly the newcomer and the
 * rerolled player: everyone else has the beats themselves, and a permanent
 * summary above the transcript would be a second, staler account of what they
 * are already reading.
 */
export function StoryPanel({
  story,
  transcriptLength,
}: {
  readonly story: string | null;
  readonly transcriptLength: number;
}) {
  if (!story || transcriptLength > NEW_ARRIVAL_BEATS) return null;

  return (
    <section
      aria-label="The story so far"
      className="rounded-md border border-neutral-300 p-3 text-sm dark:border-neutral-700"
    >
      <h2 className="font-semibold">The story so far</h2>
      <p className="mt-1 text-neutral-700 dark:text-neutral-300">{story}</p>
    </section>
  );
}

/**
 * How short a transcript counts as "no scrollback".
 *
 * Not zero: a rerolled player's window opens at their arrival line, so they
 * have one or two beats and still know nothing. A handful rather than a
 * precise figure, because the question is "has this player read any of the
 * campaign", and there is no exact answer to it.
 */
const NEW_ARRIVAL_BEATS = 5;
